import React from "react";
import { Link } from "react-router-dom";
import { Compass } from "lucide-react";
import { useContent } from "@/hooks/useContent";
import PageSEO from "@/components/PageSEO";

const IMG = "https://images.unsplash.com/photo-1568489711036-9c94a7d5aea6?crop=entropy&cs=srgb&fm=jpg&q=85";

const sections = [
  {
    t: "Purpose",
    body: "Celestial Seed Sanctuary exists to seed and steward regenerative culture — a living demonstration that human beings, land, education, healing, culture, and community can flourish together as one system.",
  },
  {
    t: "Mission",
    body: "To operate a living institution — sanctuary, school, ecovillage, healing center, cultural laboratory, and global alliance — that welcomes people into a coherent way of living, learning, and belonging.",
  },
  {
    t: "Vision",
    body: "A world in which sanctuaries, ecovillages, and schools are woven into a global fabric — where any person can find their tribe, their teacher, their calling, and a piece of land to belong to. Celestial Seed is the first node in that fabric; NEOS is its operating system.",
  },
  {
    t: "Core Principles",
    body: "Reverence · Regeneration · Reciprocity · Rigour · Rest · Rooted Action · Radical Hospitality · Beauty as Function · Craft over Speed · Truth-telling · Non-extraction · Intergenerational Time.",
  },
  {
    t: "Organizational Structure",
    body: "The institution is organized as one trunk (Celestial Seed Sanctuary) and thirteen working branches (Healing, Education, Ecology, Culture & Arts, Community, Development, Media, Funding, Global Alliance, Research, Administration, Archives & Legacy, Observatory · Learning & Evaluation). NEOS is the shared nervous system connecting all branches. Each branch has leadership, budget, and long-term arc. With the Observatory in place, the institutional architecture is complete. Future growth is deepening — not expansion.",
  },
  {
    t: "Guiding Sequence",
    body: "Knowledge precedes memory. Memory precedes intelligence. Intelligence precedes autonomy. We populate the institution with real knowledge before we build the systems that remember it, and we build the memory before we build the intelligence that reasons over it. This sequence is the discipline that keeps NEOS from becoming impressive but empty.",
  },
  {
    t: "Governance Philosophy",
    body: "Decision-making is stewardship, not ownership. Authority follows responsibility. Decisions of consequence are made in circle, documented in the Knowledge Vault, and reviewed against this Charter. The founder holds vision; the community holds practice; the land holds the final word.",
  },
  {
    t: "Educational Philosophy",
    body: "We teach by doing. Apprenticeship over lecture. Season over semester. Guild over classroom. Every visitor is both student and teacher. Knowledge that cannot be practiced is not yet knowledge.",
  },
  {
    t: "Ecological Philosophy",
    body: "The land is not a backdrop for our work — it is our first teacher and first stakeholder. Watershed, soil, forest, and wildlife have veto power. Every intervention must leave the system more alive than it found it.",
  },
  {
    t: "Cultural Philosophy",
    body: "Art is not a department. Art is the language through which every department expresses itself. Music, architecture, craft, ceremony, photography, film, storytelling, and public festival are how the mission becomes visible. Culture is treated as a regenerative force equal to ecology and education.",
  },
  {
    t: "Relationship to Volunteers",
    body: "Volunteers are members, not guests. Their work is real, their hours are honest, and their contribution is remembered in the institutional record. Room, board, training, and belonging are given in return.",
  },
  {
    t: "Relationship to Land",
    body: "The land is held in trust for the next seven generations. We do not extract from it, monetize it, or sub-divide it in ways that diminish its wholeness. Every acre added to Celestial Seed enters this same covenant.",
  },
  {
    t: "Relationship to Communities",
    body: "We are neighbours before we are an institution. Our first accountabilities are to the local communities of Cundinamarca, Colombia. Partnership, reciprocity, cultural respect, and shared benefit precede program design.",
  },
  {
    t: "Relationship to Future Generations",
    body: "We build for a hundred-year horizon. Every system, structure, agreement, and document is designed to outlast the founder. The institution belongs to those not yet born as much as to those alive today.",
  },
  {
    t: "The Archive Covenant",
    body: "Every enduring institution — universities, monasteries, museums, indigenous nations — keeps an archive. Ours will too. Founding documents, oral histories, land history, design iterations, grant history, annual reports, curriculum versions, and major decisions with their rationale are preserved in the Archives & Legacy department. Nothing of lasting value is ever discarded. Nothing of institutional consequence is left undocumented. Archives are the bones that let the institution stand upright long after any single steward has gone.",
  },
  {
    t: "Long-Term Commitments",
    body: "To operate transparently, document rigorously, welcome sincerely, teach freely, steward humbly, and grow slowly. To resist scale that costs coherence. To treat NEOS as public infrastructure that other regenerative institutions may one day inherit.",
  },
];

export default function Charter() {
  const { t } = useContent("charter");
  return (
    <div>
      <PageSEO
        title={"Institutional Charter | Celestial Seed Sanctuary"}
        description={"The constitutional identity of Celestial Seed Sanctuary. Mission, vision, principles, and commitments that shape our long-term institutional work."}
      />
      <section className="relative overflow-hidden grain">
        <div className="absolute inset-0">
          <img src={IMG} alt="Cloud forest" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-forest/60 via-forest/40 to-bone" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-40 text-bone">
          <Compass className="h-6 w-6 mb-6" strokeWidth={1.5} />
          <div className="overline text-bone/80 mb-4" data-testid="charter-overline">{t("hero.overline", "Foundational Document")}</div>
          <h1 className="font-serif text-5xl lg:text-7xl leading-[1.05] tracking-tight max-w-4xl" data-testid="charter-title">
            {t("hero.title", "The Institutional Charter of Celestial Seed Sanctuary.")}
          </h1>
          <p className="text-bone/85 text-lg mt-8 max-w-2xl leading-relaxed" data-testid="charter-lead">
            {t("hero.lead", "Not a legal document. An identity document. This Charter is the constitutional reference point for every future decision, every new department, every partnership, and every volunteer.")}
          </p>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-20 space-y-14">
        {sections.map((s, i) => (
          <article key={s.t} className="grid md:grid-cols-[80px_1fr] gap-6 md:gap-10 border-t border-line pt-10">
            <div className="font-serif text-2xl text-terracotta leading-none">{String(i + 1).padStart(2, "0")}</div>
            <div>
              <h2 className="font-serif text-3xl text-ink leading-tight">{s.t}</h2>
              <p className="mt-4 text-ink text-lg leading-relaxed">{s.body}</p>
            </div>
          </article>
        ))}
      </section>

      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-16 text-center">
        <div className="overline mb-3" data-testid="charter-witness-overline">{t("witness.overline", "In witness")}</div>
        <p className="font-serif text-2xl text-ink-soft italic leading-relaxed" data-testid="charter-witness-quote">
          {t("witness.quote", "\"The institution belongs to those not yet born as much as to those alive today.\"")}
        </p>
        <div className="mt-10">
          <Link to="/about" data-testid="charter-back-about" className="pill pill-outline">Return to The Institution</Link>
        </div>
      </section>
    </div>
  );
}
